/**
 * FadeHandle Component
 * Draggable control for adjusting fade in/out on audio clips
 */
import { useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

export const FadeHandle = ({
  type = 'fadeIn', // 'fadeIn' | 'fadeOut'
  position = 0, // Position as percentage (0-100)
  onDrag,
  onDragStart,
  onDragEnd,
  isDragging = false,
}) => {
  const handleRef = useRef(null);
  const lastX = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    lastX.current = e.clientX;
    onDragStart?.();
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - lastX.current;
      lastX.current = moveEvent.clientX;
      onDrag?.(moveEvent, deltaX);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      onDragEnd?.();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onDrag, onDragStart, onDragEnd]);

  // Touch support
  const handleTouchStart = useCallback((e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    lastX.current = touch.clientX;
    onDragStart?.();
    
    const handleTouchMove = (moveEvent) => {
      const touchMove = moveEvent.touches[0];
      const deltaX = touchMove.clientX - lastX.current;
      lastX.current = touchMove.clientX;
      onDrag?.(moveEvent, deltaX);
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      onDragEnd?.();
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [onDrag, onDragStart, onDragEnd]);

  const isLeft = type === 'fadeIn';

  return (
    <motion.div
      ref={handleRef}
      className={`
        absolute top-2 z-10
        w-4 h-4 rounded-full 
        bg-pink-400 
        shadow-[0_0_10px_rgba(244,114,182,0.5)]
        cursor-ew-resize
        transition-transform
        hover:scale-125
        active:scale-110
        ${isDragging ? 'scale-125' : ''}
      `}
      style={{
        left: isLeft ? `calc(${position}% - 8px)` : 'auto',
        right: !isLeft ? `calc(${100 - position}% - 8px)` : 'auto',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      whileHover={{ scale: 1.25 }}
      whileTap={{ scale: 1.1 }}
      data-testid={`fade-handle-${type}`}
    >
      {/* Tooltip showing fade duration */}
      {isDragging && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-zinc-800 rounded text-[10px] font-mono text-pink-300 whitespace-nowrap">
          {type === 'fadeIn' ? 'Fade In' : 'Fade Out'}
        </div>
      )}
      
      {/* Connection line to waveform */}
      <div 
        className={`
          absolute top-full w-px h-[calc(100%-16px)] bg-pink-400/50
          ${isLeft ? 'left-1/2' : 'left-1/2'}
        `}
        style={{ height: '60px' }}
      />
    </motion.div>
  );
};

export default FadeHandle;
